/*
 * cart-extras.js
 * Custom elements for the MyPureSkin cart drawer + cart page:
 *  - <estimated-delivery> : computes and displays an estimated delivery date range
 *  - <cart-discount>      : lets the customer apply a discount code via /cart/update.js
 *  - .cart-promo-gifts    : locks a gift card while its add request is in flight
 *  - <plan-qty-guard>     : keeps "N boxes every …" subscription lines at quantity N
 * The custom elements re-run on AJAX cart refresh because they upgrade when re-inserted.
 */
(function () {
  /* The codes the AJAX cart currently applies, as it reports them back:
       discount_codes: [{ code: 'SAVE10', applicable: true }, ...]
     We keep only `applicable` codes — the ones actually discounting the cart right
     now, which includes order-level, product-targeted and free-shipping codes alike.
     This is the authoritative set to re-send: `cart.cart_level_discount_applications`
     in Liquid carries order-level codes but omits product- and shipping-targeted ones,
     so we read the cart over the wire rather than trust the Liquid seed. */
  function appliedDiscountCodes(cart) {
    var entries = (cart && cart.discount_codes) || [];
    var codes = [];
    for (var i = 0; i < entries.length; i++) {
      if (entries[i] && entries[i].applicable && entries[i].code) {
        codes.push(String(entries[i].code));
      }
    }
    return codes;
  }

  function sameCode(a, b) {
    return a.toUpperCase() === b.toUpperCase();
  }

  function codeIsPresent(codes, target) {
    return codes.some(function (code) {
      return sameCode(code, target);
    });
  }

  /* Ground truth for the codes already on the cart. `/cart/update.js` replaces the
     whole set, so we must re-send them; reading them from the AJAX cart (not the Liquid
     seed) is what keeps product- and shipping-targeted codes from being silently
     dropped. Resolves to null on failure so the caller can fall back to the seed. */
  function fetchCart() {
    return fetch(theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  /* POST the new code set, bundling the same sections cart.js re-renders. Throws on a
     non-2xx so the caller surfaces a generic error rather than a half-applied state. */
  function postDiscount(codes, sections) {
    var body = JSON.stringify({ discount: codes.join(','), sections: sections });
    return fetch(
      theme.routes.cart_update_url,
      Object.assign({}, theme.utils.fetchConfig(), { body: body })
    ).then(function (response) {
      return response.json().then(function (cart) {
        if (!response.ok) throw new Error('cart-update-failed');
        return cart;
      });
    });
  }

  /* -------- Estimated delivery -------- */
  if (!customElements.get('estimated-delivery')) {
    class EstimatedDelivery extends HTMLElement {
      connectedCallback() {
        try {
          this.render();
        } catch (e) {
          /* fail silently — never block the cart */
        }
      }

      get minDays() {
        return parseInt(this.getAttribute('data-min-days'), 10) || 5;
      }

      get maxDays() {
        return parseInt(this.getAttribute('data-max-days'), 10) || 10;
      }

      get locale() {
        var candidate =
          this.getAttribute('data-locale') ||
          document.documentElement.getAttribute('lang') ||
          undefined;
        if (!candidate) return undefined;
        // Guard against malformed tags (e.g. a locale name instead of a code),
        // which would make toLocaleDateString / Intl throw and blank the date.
        try {
          Intl.DateTimeFormat.supportedLocalesOf(candidate);
          return candidate;
        } catch (e) {
          return undefined;
        }
      }

      addBusinessDays(from, days) {
        var d = new Date(from.getTime());
        var added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          var day = d.getDay();
          if (day !== 0 && day !== 6) added++;
        }
        return d;
      }

      render() {
        var target = this.querySelector('[data-delivery-range]');
        if (!target) return;

        // Normalise so a mis-configured min > max never renders a backwards range.
        var lo = Math.min(this.minDays, this.maxDays);
        var hi = Math.max(this.minDays, this.maxDays);

        var today = new Date();
        var start = this.addBusinessDays(today, lo);
        var end = this.addBusinessDays(today, hi);

        var locale = this.locale;
        var dayOpts = { month: 'short', day: 'numeric' };
        var text;
        if (lo === hi) {
          text = start.toLocaleDateString(locale, dayOpts);
        } else {
          try {
            if (typeof Intl !== 'undefined' && Intl.DateTimeFormat.prototype.formatRange) {
              text = new Intl.DateTimeFormat(locale, dayOpts).formatRange(start, end);
            } else {
              text =
                start.toLocaleDateString(locale, dayOpts) +
                ' – ' +
                end.toLocaleDateString(locale, dayOpts);
            }
          } catch (e) {
            text =
              start.toLocaleDateString(locale, dayOpts) +
              ' – ' +
              end.toLocaleDateString(locale, dayOpts);
          }
        }
        target.textContent = text;
        target.removeAttribute('aria-hidden');
        this.classList.add('cart-delivery--ready');
      }
    }
    customElements.define('estimated-delivery', EstimatedDelivery);
  }

  /* -------- Discount code -------- */
  if (!customElements.get('cart-discount')) {
    class CartDiscount extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="text"], input[type="search"]');
        this.button = this.querySelector('[data-discount-apply]');
        this.errorElement = this.querySelector('[data-discount-error]');
        this.detailsElement = this.querySelector('.cart-discount__details');
        this.summaryToggle = this.querySelector('.cart-discount__summary');
        this.onApply = this.apply.bind(this);
        this.onKey = this.handleKey.bind(this);
        if (this.button) this.button.addEventListener('click', this.onApply);
        if (this.input) this.input.addEventListener('keydown', this.onKey);
        // Tie the live region to the input so the message stays reachable when the
        // field is re-focused, not only in the instant it is first announced.
        if (this.errorElement && this.input) {
          if (!this.errorElement.id) {
            this.errorElement.id = 'cart-discount-error-' + ++CartDiscount.uid;
          }
          var described = (this.input.getAttribute('aria-describedby') || '')
            .split(' ')
            .filter(Boolean);
          if (described.indexOf(this.errorElement.id) === -1) {
            described.push(this.errorElement.id);
            this.input.setAttribute('aria-describedby', described.join(' '));
          }
        }
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onApply);
        if (this.input) this.input.removeEventListener('keydown', this.onKey);
      }

      /* Only a fallback for when fetchCart() fails: the order-level codes Liquid can
         see. Product- and shipping-targeted codes never appear here, which is exactly
         why the AJAX cart is the primary source. */
      get appliedCodes() {
        var raw = this.getAttribute('data-applied-codes') || '';
        return raw
          .split(',')
          .map(function (code) {
            return code.trim();
          })
          .filter(Boolean);
      }

      setBusy(busy) {
        this.busy = busy;
        if (!this.button) return;
        if (busy) this.button.setAttribute('aria-busy', 'true');
        else this.button.removeAttribute('aria-busy');
      }

      /* Reveal the panel (a section re-render leaves it collapsed) and tie the message
         to the input for assistive tech. tone 'info' is a neutral notice, not a fault. */
      showNotice(message, tone) {
        if (!this.errorElement) return;
        // Reveal the panel BEFORE writing the text: the live region sits inside the
        // <details>, and a collapsed <details> keeps its body out of the accessibility
        // tree, so a text change made while hidden would not be announced.
        if (this.detailsElement) this.detailsElement.open = true;
        this.errorElement.classList.toggle('cart-discount__error--info', tone === 'info');
        if (this.input) {
          if (tone === 'error') this.input.setAttribute('aria-invalid', 'true');
          else this.input.removeAttribute('aria-invalid');
        }
        this.errorElement.textContent = message;
      }

      clearNotice() {
        if (this.errorElement) {
          this.errorElement.textContent = '';
          this.errorElement.classList.remove('cart-discount__error--info');
        }
        if (this.input) this.input.removeAttribute('aria-invalid');
      }

      /* A concurrent cart mutation can replace this element mid-request; after an
         await, prefer the live replacement so a message never lands on a detached node. */
      liveTwin() {
        return this.isConnected ? this : document.querySelector('cart-discount');
      }

      /* A section re-render destroys this element and its live region, and a live
         region only announces content that changes after it exists. So publish first
         (the re-render is synchronous), then one frame later — the region now in the
         tree — show the message on the live replacement and pull focus back to the
         discount control instead of the product row the re-render focused. */
      publishWithNotice(cart, message, tone) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-discount',
          cart: cart
        });
        requestAnimationFrame(function () {
          // One <cart-discount> per page (the drawer is gated off on /cart at
          // cart-drawer.liquid:1, and main-cart renders only on /cart), so resolving it
          // fresh here always targets the current live element — never a detached one.
          var fresh = document.querySelector('cart-discount');
          if (!fresh) return;
          if (message) fresh.showNotice(message, tone);
          var focusTarget = message && tone === 'error' ? fresh.input : fresh.summaryToggle;
          if (focusTarget && typeof focusTarget.focus === 'function') {
            try {
              focusTarget.focus();
            } catch (e) {
              /* focusing a collapsed/hidden control can throw — never block on it */
            }
          }
        });
      }

      handleKey(event) {
        if (event.key !== 'Enter') return;
        // The keydown that commits an IME composition also reports Enter; acting on it
        // would submit half-composed text as a discount code.
        if (event.isComposing || event.keyCode === 229) return;
        event.preventDefault();
        if (event.repeat) return;
        this.apply();
      }

      async apply() {
        if (!this.input || this.busy) return;
        var code = this.input.value.trim();
        if (!code) {
          this.input.focus();
          return;
        }
        this.clearNotice();
        this.setBusy(true);

        try {
          var prior = await fetchCart();
          var priorCodes = prior ? appliedDiscountCodes(prior) : this.appliedCodes;

          // Already on the cart — no request, just say so.
          if (prior && codeIsPresent(priorCodes, code)) {
            var applied = this.liveTwin();
            if (applied) {
              applied.showNotice(theme.cartDiscountStrings.alreadyApplied, 'info');
              if (applied.input) applied.input.select();
            }
            return;
          }

          // `discount` replaces the whole set, so re-send the existing codes with the
          // new one or the earlier ones drop off.
          var codes = priorCodes.filter(function (existing) {
            return !sameCode(existing, code);
          });
          codes.push(code);

          // Same section-bundling contract cart.js uses, so the drawer and cart page
          // re-render from this one response.
          var sections = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
          );

          var cart = await postDiscount(codes, sections);

          var responseCodes = appliedDiscountCodes(cart);
          var newAccepted = codeIsPresent(responseCodes, code);
          // Codes we tried to preserve that the server dropped anyway (incompatible
          // combination) — a real conflict the customer must be told about.
          var dropped = priorCodes.filter(function (existing) {
            return !sameCode(existing, code) && !codeIsPresent(responseCodes, existing);
          });
          // A code can apply yet not touch cart pricing (free shipping surfaces at
          // checkout); detect that so a real success is not left looking like nothing.
          var priceChanged = prior
            ? cart.total_price !== prior.total_price || cart.total_discount !== prior.total_discount
            : true;

          if (newAccepted) {
            if (dropped.length) {
              this.publishWithNotice(cart, theme.cartDiscountStrings.conflict, 'error');
            } else if (!priceChanged) {
              this.publishWithNotice(cart, theme.cartDiscountStrings.shippingNotice, 'info');
            } else {
              this.publishWithNotice(cart, null, null);
            }
            return;
          }

          // The new code did not take effect.
          if (dropped.length || priceChanged) {
            // Something else moved server-side (e.g. an existing code expired); re-render
            // so the totals stay truthful, and report on the replacement element.
            this.publishWithNotice(cart, theme.cartDiscountStrings.invalid, 'error');
          } else {
            // Nothing changed for the customer — keep the field open to fix the code.
            var stale = this.liveTwin();
            if (stale) {
              stale.showNotice(theme.cartDiscountStrings.invalid, 'error');
              if (stale.input) stale.input.select();
            }
          }
        } catch (error) {
          console.error(error);
          // A concurrent cart mutation may have replaced this element mid-request, so
          // show the error on whichever cart-discount is live.
          var target = this.liveTwin();
          if (target) target.showNotice(theme.cartDiscountStrings.error, 'error');
        } finally {
          this.setBusy(false);
        }
      }
    }
    CartDiscount.uid = 0;
    customElements.define('cart-discount', CartDiscount);
  }

  /* -------- Promo gifts: one of each variant --------
     Theme ProductForm already blocks a double-click on the same button
     (aria-disabled). This Set survives the drawer re-render so a second
     click on the other card, or a re-click on a replaced card, cannot
     queue qty 2 while the first add is still in flight. */
  if (!window.__mpsPromoGiftLock) {
    window.__mpsPromoGiftLock = true;

    var promoGiftInFlight = new Set();
    var PROMO_UNLOCK_MS = 8000;

    function promoGiftVariantId(fromNode) {
      if (!fromNode || typeof fromNode.closest !== 'function') return '';
      var form = fromNode.closest('form');
      var input = form ? form.querySelector('[name="id"]') : null;
      return input && input.value ? String(input.value) : '';
    }

    function promoGiftCard(fromNode) {
      if (!fromNode || typeof fromNode.closest !== 'function') return null;
      return fromNode.closest('.cart-promo-gifts .horizontal-product');
    }

    function clearPromoGiftLocks() {
      promoGiftInFlight.clear();
      document.querySelectorAll('.cart-promo-gifts .horizontal-product.is-adding').forEach(function (card) {
        card.classList.remove('is-adding');
      });
    }

    document.addEventListener(
      'click',
      function (event) {
        var submitter =
          event.target && event.target.closest ? event.target.closest('.cart-promo-gifts [type="submit"]') : null;
        if (!submitter) return;
        var variantId = promoGiftVariantId(submitter);
        if (variantId && promoGiftInFlight.has(variantId)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || typeof form.closest !== 'function' || !form.closest('.cart-promo-gifts')) return;
        var variantId = promoGiftVariantId(form);
        if (!variantId) return;
        if (promoGiftInFlight.has(variantId)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        promoGiftInFlight.add(variantId);
        var card = promoGiftCard(form);
        if (card) card.classList.add('is-adding');
        window.setTimeout(function () {
          promoGiftInFlight.delete(variantId);
        }, PROMO_UNLOCK_MS);
      },
      true
    );

    document.addEventListener('cart:updated', clearPromoGiftLocks);
    document.addEventListener('ajaxProduct:error', clearPromoGiftLocks);
  }

  /* -------- Plan quantity guard --------
     "N boxes every …" plans fix the number of boxes: the line must stay at quantity N,
     because ShipBob ships and Seal renews the line quantity. A double add (PDP twice,
     PDP + quiz, quiz card + bundle) merges into 2N, which the locked field can't undo,
     so the guard sets the line back to N. One attempt per line until it reads N again.
     On failure it stays silent: cart.js's own error path redirects to /cart, where the
     guard would fire again and loop.
     A line it reset shows a short note (data-note) for the rest of the session, so a
     total that changed on its own (e.g. an old "3 boxes" x1 line now x3) is explained. */
  if (!customElements.get('plan-qty-guard')) {
    var planQtyPending = {};
    var PLAN_QTY_ADJUSTED_KEY = 'mps-plan-qty-adjusted';
    var planQtyAdjusted = {};
    try {
      planQtyAdjusted = JSON.parse(sessionStorage.getItem(PLAN_QTY_ADJUSTED_KEY)) || {};
    } catch (error) {}

    var markPlanQtyAdjusted = function (line) {
      planQtyAdjusted[line] = true;
      try {
        sessionStorage.setItem(PLAN_QTY_ADJUSTED_KEY, JSON.stringify(planQtyAdjusted));
      } catch (error) {}
    };

    class PlanQtyGuard extends HTMLElement {
      connectedCallback() {
        var line = this.getAttribute('data-line');
        var expected = parseInt(this.getAttribute('data-expected'), 10);
        var current = parseInt(this.getAttribute('data-quantity'), 10);
        if (!line || !expected) return;
        if (current === expected) {
          delete planQtyPending[line];
          var note = planQtyAdjusted[line] && document.getElementById(this.getAttribute('data-note'));
          if (note) note.hidden = false;
          return;
        }
        if (planQtyPending[line]) return;
        planQtyPending[line] = true;

        // Same section-bundling contract cart.js uses, so the drawer and cart page
        // re-render from this one response.
        var sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
        );

        fetch(
          theme.routes.cart_change_url,
          Object.assign({}, theme.utils.fetchConfig(), {
            body: JSON.stringify({ id: line, quantity: expected, sections: sections })
          })
        )
          .then(function (response) {
            return response.json().then(function (cart) {
              if (!response.ok || cart.errors) return;
              // Before the re-render, so the new guard for this line reveals its note
              markPlanQtyAdjusted(line);
              if (!cart.sections) return;

              // cart.js moves focus to the first line title after every update, with a plain
              // focus(): if the drawer is still sliding in, that scrolls the locked page behind
              // it (page shifted up until the drawer closes). The guard is not a customer
              // action, so the trap is rebuilt as cart.js does but focus stays where it is.
              var trapFocus = theme.a11y.trapFocus;
              theme.a11y.trapFocus = function (container) {
                trapFocus(container, document.activeElement || container);
              };
              try {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                  source: 'plan-qty-guard',
                  cart: cart
                });
              } finally {
                theme.a11y.trapFocus = trapFocus;
              }
            });
          })
          .catch(function (error) {
            console.error(error);
          });
      }
    }
    customElements.define('plan-qty-guard', PlanQtyGuard);
  }
})();
